// src/services/mediaLibraryServices.js
import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/media`;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const toNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const buildQuery = (params = {}) => {
  const q = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (typeof value === "boolean") {
      q.append(key, value ? "true" : "false");
      return;
    }

    if (Array.isArray(value)) {
      const joined = value
        .map((v) => String(v).trim())
        .filter(Boolean)
        .join(",");

      if (joined) q.append(key, joined);
      return;
    }

    const s = String(value).trim();
    if (s !== "") q.append(key, s);
  });

  const qs = q.toString();
  return qs ? `?${qs}` : "";
};

export const toAbsolutePublicUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;

  const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const cleanPath = String(url).startsWith("/") ? url : `/${url}`;

  return `${base}${cleanPath}`;
};

export const isImageMedia = (media) => {
  const type = String(media?.type || "").toLowerCase();
  const mime = String(media?.mime_type || "").toLowerCase();
  const url = String(media?.url || "").toLowerCase();

  return (
    type === "image" ||
    mime.startsWith("image/") ||
    /\.(jpe?g|png|webp|gif)$/i.test(url)
  );
};

export const isPdfMedia = (media) => {
  const mime = String(media?.mime_type || "").toLowerCase();
  const url = String(media?.url || "").toLowerCase();

  return mime === "application/pdf" || /\.pdf$/i.test(url);
};

export const formatFileSize = (bytes) => {
  const size = Number(bytes);

  if (!Number.isFinite(size) || size <= 0) return "—";
  if (size < 1024) return `${size} o`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} Ko`;

  return `${(size / (1024 * 1024)).toFixed(1)} Mo`;
};

export const searchMedia = async (params = {}) => {
  try {
    const qs = buildQuery(params);

    const { data } = await axios.get(`${apiUrl}${qs}`, {
      headers: authHeaders(),
    });

    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: toNumber(data?.total, 0),
      page: toNumber(data?.page, 1),
      limit: toNumber(data?.limit, 24),
      totalPages: toNumber(data?.totalPages, 1),
    };
  } catch (e) {
    console.error("Erreur searchMedia:", e);
    return { items: [], total: 0, page: 1, limit: 24, totalPages: 1 };
  }
};

export const uploadMedia = async (file, meta = {}) => {
  if (!file) {
    throw new Error("Aucun fichier à uploader.");
  }

  const formData = new FormData();
  formData.append("file", file);

  Object.entries(meta).forEach(([key, value]) => {
    if (value === undefined || value === null) return;

    if (typeof value === "boolean") {
      formData.append(key, value ? "true" : "false");
      return;
    }

    const s = String(value).trim();
    if (s !== "") formData.append(key, s);
  });

  const { data } = await axios.post(`${apiUrl}/upload`, formData, {
    headers: {
      ...authHeaders(),
    },
  });

  return data;
};

export const updateMedia = async (id, payload = {}) => {
  if (!id) throw new Error("updateMedia: id manquant.");

  const { data } = await axios.put(`${apiUrl}/${id}`, payload, {
    headers: {
      ...authHeaders(),
      "Content-Type": "application/json",
    },
  });

  return data;
};

export const deleteMedia = async (id) => {
  if (!id) throw new Error("deleteMedia: id manquant.");

  const { data } = await axios.delete(`${apiUrl}/${id}`, {
    headers: authHeaders(),
  });

  return data;
};

export const getMediaCategories = async () => {
  try {
    const { data } = await axios.get(`${apiUrl}/categories`, {
      headers: authHeaders(),
    });

    return Array.isArray(data?.items) ? data.items : [];
  } catch (e) {
    console.error("Erreur getMediaCategories:", e);
    return [];
  }
};